<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useUIStore } from '@/stores/ui'
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from '@lucide/vue'

const ui = useUIStore()
const { toasts } = storeToRefs(ui)

const icons = { success: CheckCircle2, error: XCircle, warning: AlertTriangle, info: Info }
const colors = {
  success: 'bg-success-bg/95 border-success-text/20 text-success-text',
  error:   'bg-error-bg/95 border-error/20 text-error-text',
  warning: 'bg-warn-bg/95 border-warn-text/20 text-warn-text',
  info:    'bg-surface border-border-standard text-text-dark',
}
const iconColors = {
  success: 'text-success-text',
  error:   'text-error',
  warning: 'text-warn-text',
  info:    'text-primary',
}
</script>

<template>
  <teleport to="body">
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      class="fixed top-4 right-4 sm:top-6 sm:right-6 z-toast flex flex-col gap-3 w-full max-w-sm pointer-events-none"
    >
      <transition-group
        name="toast"
        tag="div"
        class="flex flex-col gap-3 items-end"
      >
        <div
          v-for="t in toasts"
          :key="t.id"
          class="flex items-start gap-3 border rounded-card p-4 shadow-floating backdrop-blur-md pointer-events-auto"
          :class="colors[t.type]"
        >
          <component
            :is="icons[t.type]"
            class="w-5 h-5 shrink-0 mt-1"
            :class="iconColors[t.type]"
          />
          <div class="flex-1 min-w-0">
            <p
              data-testid="toast-title"
              class="font-bold text-sm leading-tight text-text-dark"
            >
              {{ t.title }}
            </p>
            <p
              v-if="t.message"
              data-testid="toast-message"
              class="text-xs mt-1 text-text-muted leading-snug"
            >
              {{ t.message }}
            </p>
          </div>
          <button
            aria-label="Tutup"
            class="shrink-0 text-text-muted hover:text-text-dark hover:bg-black/5 p-1.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
            @click="ui.dismissToast(t.id)"
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<style scoped>
.toast-enter-active {
  transition: opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.toast-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 1, 1), transform 0.3s cubic-bezier(0.4, 0, 1, 1);
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(120px) scale(0.95);
}
.toast-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.97);
}
</style>
